/**
 * Map Sites — NEW standalone feature (docs/sites/README.md).
 *
 * Not to be confused with the legacy mission-era `sites.ts` (`/v2/site`).
 * These functions target the new `/v3/site` routes and are exported
 * both individually and grouped as `mapSitesAPI`.
 */

import { config } from '../config';
import { headers, APIResponse, stleaf, fetchAPI, SpanTag } from '../helpers';

export type MapSiteBoundarySource = 'location' | 'upload';
export type MapSiteReferenceSetKind = 'gcp' | 'prism';

/** Geocode viewport, same shape as locations.bbox: [[ne lng, ne lat], [sw lng, sw lat]] */
export type MapSiteBbox = [[number, number], [number, number]];

export interface MapSiteListItem {
  id: string;
  /**
   * The workspace that OWNS the site. A site's reference sets may only be added
   * to maps in this same workspace — the point layers hold the mark, which
   * resolves back through this site.
   */
  organisationId: string;
  name: string;
  address: string | null;
  s3ImageUrl: string | null;
  /** Latest map cover — fallback when no site image was uploaded */
  coverUrl: string | null;
  mapsCount: number;
  referenceSetsCount: number;
  lastActivityAt: string | null;
  /** Display name of the site's creator (site cards in the Map Browser). */
  createdByName: string | null;
  /** Set when the site has been archived (soft delete); null = active. */
  archivedAt: string | null;
}

export interface MapSite extends MapSiteListItem {
  center: [number, number] | null;
  bbox: MapSiteBbox | null;
  /** GeoJSON polygon/multipolygon */
  boundary: Record<string, unknown> | null;
  boundarySource: MapSiteBoundarySource;
}

export interface CreateMapSiteParams {
  name: string;
  address: string;
  center: [number, number];
  bbox?: MapSiteBbox | null;
  boundarySource: MapSiteBoundarySource;
  /**
   * Parsed client-side from an uploaded boundary file — .geojson/.json are
   * parsed directly; .kml is converted to GeoJSON in the browser (the API
   * only accepts GeoJSON).
   */
  boundaryGeoJSON?: Record<string, unknown>;
  s3ImageUrl?: string;
}

export interface UpdateMapSiteParams {
  name?: string;
  address?: string;
  center?: [number, number];
  bbox?: MapSiteBbox | null;
  /** null clears the uploaded site photo. */
  s3ImageUrl?: string | null;
}

export interface MapSiteMapRow {
  id: string;
  name: string;
  coverUrl: string | null;
  address: string | null;
  createdBy: { name: string | null } | null;
  capturedAt: string | null;
  filesCount: number | null;
  sizeBytes: number | null;
  workspaceName: string | null;
  folderName: string | null;
  autoGrouped: boolean;
  /** When the map was assigned to the site (drives the auto-grouped banner) */
  addedAt: string;
}

export interface MapSiteMatchCandidate {
  siteId: string;
  name: string;
}

/** A DEM (singleband_cog raster) on one of a site's maps — a "bring across" candidate. */
export interface SiteDemLayer {
  layerId: string;
  mapId: string;
  mapName: string;
  /** The layer's own name (e.g. "DTM_5cm"). */
  name: string;
  /** The owning map's capture date — the epoch shown to users. */
  capturedAt: string;
  createdAt: string;
}

/**
 * Record-only "bring across" selection stored against a map when it's created
 * from a site: which site reference-set versions + prior DEM layers to carry in.
 */
export interface CarriedOverSelection {
  referenceSetVersionIds: string[];
  demLayerIds: string[];
}

export interface MapSiteReferenceSetVersion {
  id: string;
  version: number;
  description: string | null;
  s3Key: string | null;
  /**
   * CRS of this version's x,y as a curated spatial_ref_sys srid. Per version —
   * the upload modal defaults it to the previous version's (inheritance).
   */
  srid: number;
  /** Human-readable CRS name for display, e.g. "GDA94 / MGA zone 56". */
  crsName: string | null;
  /**
   * The rest of the CRS record, so a caller putting this CRS on a processing
   * job does not have to look it up (BIR-22). It cannot: `GET /v1/crs` matches
   * on `auth_srid`, so a BIRDI compound CRS — srid 200024, auth_srid 207855 —
   * never resolves by its srid.
   */
  authName: string | null;
  authSrid: number | null;
  wkt: string | null;
  proj4Text: string | null;
  crsPriority: number | null;
  /** Original uploaded filename — the "File reference" row in the toolbox. */
  fileName: string | null;
  pointsCount: number;
  /**
   * How many PDF reports across this site's maps were generated from this
   * version's points — the "Referenced by N reports" line. Counted from the
   * report layers themselves, so it drops when a report or its map is deleted.
   * Prism stays 0 until prism reports exist; the count is kind-agnostic.
   */
  referencedByCount: number;
  createdAt: string;
}

export interface MapSiteReferenceSet {
  id: string;
  kind: MapSiteReferenceSetKind;
  /** Version number of the current (highest) version */
  currentVersion: number | null;
  versions: MapSiteReferenceSetVersion[];
}

export interface UploadReferenceSetVersionParams {
  kind: MapSiteReferenceSetKind;
  description?: string;
  /**
   * REQUIRED CRS of the CSV's x,y, as a curated spatial_ref_sys srid. The
   * `id,x,y,z` format carries no projection, so the same numbers are a valid
   * point in every UTM zone — the server rejects srids outside the catalogue.
   */
  srid: number;
  /** Original filename of the picked file — shown as "File reference". */
  fileName?: string;
  /** Raw CSV content — parsed server-side into per-point rows */
  csv?: string;
  /** Alternatively, an already-uploaded S3 key */
  s3Key?: string;
}

// BIR-22: canonical mount is `/v3/site`. The API still serves `/v2/map-site`
// as a deprecated alias, so older clients keep working.
const base = () => `${config.API_URL}/v3/site`;

export const createSite = (
  st: SpanTag,
  params: CreateMapSiteParams,
): Promise<APIResponse<MapSite>> => {
  return fetchAPI(stleaf(st, 'mscre8'), base(), {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(params),
  });
};

/**
 * Sites in the workspace the user is signed into — the same scoping map
 * folders use. The primary org is itself a workspace, so it lists its own
 * sites, not its sub-orgs'.
 *
 * The filters mirror the Map Browser's dropdowns and are applied server-side,
 * so sites respond to them the same way maps do.
 */
export interface ListSitesParams {
  search?: string;
  /**
   * View another workspace's sites without switching into it. Must be a
   * workspace the user is a member of, in their own organisation tree. Omit
   * for the workspace they are signed into.
   */
  organisationId?: string;
  userId?: string;
  /**
   * Geocoder viewport as JSON `[[lng,lat],[lng,lat]]` — a site matches when its
   * boundary intersects it. A bbox, not a location id: the Map Browser's
   * location dropdown is a geocoder result, and the map search takes the same
   * value.
   */
  locationBbox?: string;
}

export const listSites = (
  st: SpanTag,
  params?: ListSitesParams,
): Promise<APIResponse<MapSiteListItem[]>> => {
  const qs = params
    ? new URLSearchParams(
        Object.fromEntries(
          Object.entries(params).filter(([, v]) => v !== undefined && v !== ''),
        ) as Record<string, string>,
      ).toString()
    : '';
  return fetchAPI(stleaf(st, 'mslist'), `${base()}${qs ? `?${qs}` : ''}`, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export const getSite = (
  st: SpanTag,
  id: string,
): Promise<APIResponse<MapSite>> => {
  return fetchAPI(stleaf(st, 'msget1'), `${base()}/${id}`, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export const updateSite = (
  st: SpanTag,
  id: string,
  params: UpdateMapSiteParams,
): Promise<APIResponse<MapSite>> => {
  return fetchAPI(stleaf(st, 'msupd8'), `${base()}/${id}`, {
    method: 'PUT',
    credentials: 'include',
    headers,
    body: JSON.stringify(params),
  });
};

/**
 * Archive a site (soft delete — the API keeps the row and its reference sets
 * so they stay recoverable; the site disappears from all reads).
 */
export const archiveSite = (
  st: SpanTag,
  id: string,
): Promise<APIResponse<void>> => {
  return fetchAPI(stleaf(st, 'msarc1'), `${base()}/${id}`, {
    method: 'DELETE',
    credentials: 'include',
    headers,
  });
};

export const listSiteMaps = (
  st: SpanTag,
  id: string,
  params?: { start?: number; limit?: number },
): Promise<APIResponse<MapSiteMapRow[]>> => {
  const qs = params ? new URLSearchParams(params as any).toString() : '';
  return fetchAPI(
    stleaf(st, 'msmaps'),
    `${base()}/${id}/maps${qs ? `?${qs}` : ''}`,
    {
      method: 'GET',
      credentials: 'include',
      headers,
    },
  );
};

export interface AssignableMap {
  id: string;
  name: string | null;
  locationName: string | null;
  coverUrl: string | null;
  /** True when the map's footprint overlaps this site's boundary. */
  matchesLocation: boolean;
}

/**
 * Maps that can be added to the site, ordered with the site's own location
 * first (footprint overlap) so the "Add map" picker surfaces likely matches at
 * the top. Excludes maps already assigned to any site.
 */
export const listAssignableMaps = (
  st: SpanTag,
  id: string,
  params?: { search?: string; start?: number; limit?: number },
): Promise<APIResponse<AssignableMap[]>> => {
  const qs = params
    ? new URLSearchParams(
        Object.fromEntries(
          Object.entries(params).filter(([, v]) => v !== undefined && v !== ''),
        ) as Record<string, string>,
      ).toString()
    : '';
  return fetchAPI(
    stleaf(st, 'msamap'),
    `${base()}/${id}/assignable-maps${qs ? `?${qs}` : ''}`,
    {
      method: 'GET',
      credentials: 'include',
      headers,
    },
  );
};

/**
 * Add/assign a map to the site. Reassigns if the map already belongs to
 * another site (also the accept side of "Change site").
 *
 * `carriedOver` is only passed when the map was just created from this site
 * (the New map modal) — the reference-set versions / prior DEM layers the user
 * chose to bring across, which the API materialises as layers on the new map.
 * Omitted for plain add/change-site.
 *
 * A set that can't be added (e.g. a version uploaded before per-point storage,
 * so it has no points) is skipped rather than failing the call, and comes back
 * in `skippedReferenceSets`.
 */
export const addMapToSite = (
  st: SpanTag,
  id: string,
  mapId: string,
  carriedOver?: CarriedOverSelection,
): Promise<APIResponse<void>> => {
  return fetchAPI(stleaf(st, 'msadd1'), `${base()}/${id}/maps`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(carriedOver ? { mapId, carriedOver } : { mapId }),
  });
};

/** DEM layers across the site's maps — candidates for "bring across a DEM". */
export const listSiteDemLayers = (
  st: SpanTag,
  id: string,
): Promise<APIResponse<SiteDemLayer[]>> => {
  return fetchAPI(stleaf(st, 'msdems'), `${base()}/${id}/dem-layers`, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export const removeMapFromSite = (
  st: SpanTag,
  id: string,
  mapId: string,
): Promise<APIResponse<void>> => {
  return fetchAPI(stleaf(st, 'msrem1'), `${base()}/${id}/maps/${mapId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers,
  });
};

/**
 * The site a map is currently assigned to, or `{ site: null }` when it is in
 * none. Pre-selects the current site in the map browser's "Add to site" modal —
 * the maps list itself doesn't carry a map's site.
 */
export const getSiteForMap = (
  st: SpanTag,
  mapId: string,
): Promise<APIResponse<{ site: { id: string; name: string } | null }>> => {
  return fetchAPI(stleaf(st, 'ms4map'), `${base()}/for-map/${mapId}`, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

/** Auto-group candidates for a map (PostGIS bbox/boundary overlap). */
export const matchSitesForMap = (
  st: SpanTag,
  mapId: string,
): Promise<APIResponse<{ candidates: MapSiteMatchCandidate[] }>> => {
  return fetchAPI(stleaf(st, 'msmtch'), `${base()}/match`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({ mapId }),
  });
};

/** A map's site and that site's reference sets, when the map is in a site. */
export interface MapSiteReferenceSetsForMap {
  /** `null` when the map belongs to no site — the ordinary case. */
  site: { id: string; name: string } | null;
  /** Empty when `site` is null. */
  referenceSets: MapSiteReferenceSet[];
}

/**
 * The site a map belongs to plus its reference sets, in one call (BIR-22).
 *
 * The processing request form needs both facts before its first render — one
 * decides whether the site-sourced sections exist at all, the other fills them
 * — so fetching them separately would flicker between two layouts.
 */
export const getReferenceSetsForMap = (
  st: SpanTag,
  mapId: string,
): Promise<APIResponse<MapSiteReferenceSetsForMap>> => {
  return fetchAPI(
    stleaf(st, 'msrefs4map'),
    `${base()}/reference-sets?mapId=${encodeURIComponent(mapId)}`,
    {
      method: 'GET',
      credentials: 'include',
      headers,
    },
  );
};

/** Both reference-set kinds with current version + full version history. */
export const getReferenceSets = (
  st: SpanTag,
  id: string,
): Promise<APIResponse<MapSiteReferenceSet[]>> => {
  return fetchAPI(stleaf(st, 'msrefs'), `${base()}/${id}/reference-sets`, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

/** Upload a new version — versions supersede; version = max + 1. */
export const uploadReferenceSetVersion = (
  st: SpanTag,
  id: string,
  params: UploadReferenceSetVersionParams,
): Promise<APIResponse<MapSiteReferenceSetVersion>> => {
  return fetchAPI(stleaf(st, 'msrfup'), `${base()}/${id}/reference-sets`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(params),
  });
};

/**
 * Add reference-set versions onto a map as point layers (Map Browser →
 * "+ Add selected files").
 *
 * Batch on purpose: one call creates ONE folder named after the site holding
 * every set passed in, so sets picked together land together. Calling it again
 * for the same site creates another folder rather than merging into the first.
 *
 * A version with no stored points (an upload from before per-point storage)
 * can't be added and comes back in `skipped` instead of failing the call.
 */
export const addReferenceSetsToMap = (
  st: SpanTag,
  siteId: string,
  versionIds: string[],
  mapId: string,
): Promise<
  APIResponse<{
    folderId: string | null;
    added: { versionId: string; layerId: string; pointLayerIds: string[] }[];
    skipped: { versionId: string; kind: string; reason: string }[];
  }>
> => {
  return fetchAPI(
    stleaf(st, 'msrf2m'),
    `${base()}/${siteId}/reference-sets/add-to-map`,
    {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify({ mapId, versionIds }),
    },
  );
};


/** Survey point types — the map icon's SHAPE is chosen from this. */
export type ReferencePointType =
  | 'prism'
  | 'gcp'
  | 'check_point'
  | 'elevation_point'
  | 'max_elevation'
  | 'min_elevation'
  | 'tlz_area'
  | 'auscors_site'
  | 'psm_location';

/** Drives the icon COLOUR. */
export type ReferencePointStatus =
  | 'active'
  | 'destroyed'
  | 'warning'
  /** Surveyed but not yet installed on the ground. */
  | 'planned';

/** GCP-side only: a control point establishes accuracy, a check point verifies it. */
export type ReferencePointRole = 'control' | 'check';

/** One surveyed point of a reference-set version. */
export interface ReferencePoint {
  id: string;
  versionId: string;
  /** The surveyor's own identifier, e.g. "GCP01" / "9003". */
  label: string;
  type: ReferencePointType;
  role: ReferencePointRole | null;
  status: ReferencePointStatus;
  /** Prism-side grouping, e.g. "A" / "C". */
  embankment: string | null;
  /** Source coordinates in the version's CRS (Easting / Northing / Elevation). */
  x: number;
  y: number;
  z: number | null;
  /** Reprojected to WGS84 at upload, used to draw the point. */
  lng: number;
  lat: number;
}

/** Points of a version — feeds the table view and the per-point layers. */
export const listReferencePoints = (
  st: SpanTag,
  siteId: string,
  versionId: string,
): Promise<APIResponse<ReferencePoint[]>> => {
  return fetchAPI(
    stleaf(st, 'msrfpt'),
    `${base()}/${siteId}/reference-sets/${versionId}/points`,
    {
      method: 'GET',
      credentials: 'include',
      headers,
    },
  );
};

/**
 * Update a point's status from the layer toolbox — the one point attribute
 * users own after upload.
 */
export const updateReferencePointStatus = (
  st: SpanTag,
  pointId: string,
  status: ReferencePointStatus,
): Promise<APIResponse<ReferencePoint>> => {
  return fetchAPI(
    stleaf(st, 'msrfst'),
    `${base()}/reference-points/${pointId}/status`,
    {
      method: 'PUT',
      credentials: 'include',
      headers,
      body: JSON.stringify({ status }),
    },
  );
};

/**
 * Set a prism's embankment — the grouping label the report rolls up by. Like
 * status, it belongs to the MARK, so the edit holds across every survey.
 * Send null (or an empty string) to clear it.
 */
export const updateReferencePointEmbankment = (
  st: SpanTag,
  pointId: string,
  embankment: string | null,
): Promise<APIResponse<ReferencePoint>> => {
  return fetchAPI(
    stleaf(st, 'msrfem'),
    `${base()}/reference-points/${pointId}/embankment`,
    {
      method: 'PUT',
      credentials: 'include',
      headers,
      body: JSON.stringify({ embankment }),
    },
  );
};

// ---------------------------------------------------------------------------
// Tolerances
// ---------------------------------------------------------------------------

export type ToleranceAxis = 'horizontal' | 'vertical';

export interface ToleranceLevel {
  axis: ToleranceAxis;
  /** 'Warning' | 'Action' today; free text so more levels can be added. */
  name: string;
  /** Metres. The level applies from this value upward. */
  threshold: number;
  /** Null means "colour by rank" rather than a stored colour. */
  colour: string | null;
}

/** What the client sends: colour is optional, everything else required. */
export interface ToleranceLevelInput {
  axis: ToleranceAxis;
  name: string;
  threshold: number;
  colour?: string | null;
}

export interface ToleranceVersion {
  id: string;
  siteId: string;
  createdBy: string | null;
  /** "Last changed by [name]"; null once the author's user row is gone. */
  createdByName: string | null;
  createdAt: string;
  /** Ordered by threshold within an axis, so array order IS rank order. */
  levels: ToleranceLevel[];
}

export interface ToleranceResponse {
  /** Null when the site has never been configured. */
  current: ToleranceVersion | null;
  /**
   * What to show when `current` is null. Sent by the API rather than repeated
   * here so the empty state and the report can't disagree about the defaults.
   * Level shape, but colourless — the defaults are ranked, not coloured.
   */
  defaults: ToleranceLevelInput[];
}

/** The tolerance in force for a site, plus the defaults to fall back on. */
export const getTolerances = (
  st: SpanTag,
  id: string,
): Promise<APIResponse<ToleranceResponse>> => {
  return fetchAPI(stleaf(st, 'mstolg'), `${base()}/${id}/tolerances`, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

/** Every version, newest first — the "View history" list. */
export const getToleranceHistory = (
  st: SpanTag,
  id: string,
): Promise<APIResponse<ToleranceVersion[]>> => {
  return fetchAPI(stleaf(st, 'mstolh'), `${base()}/${id}/tolerances/history`, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

/**
 * Record a new version. Always the FULL set of levels, never a patch — a
 * version is a complete statement of the limits in force.
 */
export const saveTolerances = (
  st: SpanTag,
  id: string,
  levels: ToleranceLevelInput[],
): Promise<APIResponse<ToleranceVersion>> => {
  return fetchAPI(stleaf(st, 'mstols'), `${base()}/${id}/tolerances`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({ levels }),
  });
};

/** One survey the report can cover: the baseline, or a completed processing job. */
export interface PrismEpochOption {
  /** Null on the surveyed baseline; a processing job id otherwise. */
  processingJobId: string | null;
  /** Stable key — the job id, or 'baseline'. */
  key: string;
  /** ISO. Exactly the date the PDF prints for this epoch. */
  date: string;
  /** The job's name, when it has one. */
  name: string | null;
  srid: number;
  /** Marks measured at this epoch — worth seeing before generating. */
  marksObserved: number;
  isBaseline: boolean;
  reprojected: boolean;
}

/**
 * The epochs a prism report would cover — the options in the "Create prism
 * report" date dropdowns.
 *
 * These are surveys, not arbitrary dates: the baseline plus one per completed
 * processing job. Read-only, so it needs no map.
 */
export const getPrismEpochs = (
  st: SpanTag,
  siteId: string,
  setId: string,
): Promise<APIResponse<{ epochs: PrismEpochOption[] }>> => {
  return fetchAPI(
    stleaf(st, 'mspreps'),
    `${base()}/${siteId}/reference-sets/${setId}/epochs`,
    {
      method: 'GET',
      credentials: 'include',
      headers,
    },
  );
};

/**
 * CSV template download URL. Auth is cookie-based, so a plain anchor href
 * works for logged-in users.
 */
/**
 * Kick off a prism trend PDF for a site's prism set.
 *
 * An epoch is the surveyed baseline or a completed processing job, so this
 * needs at least two of them — the API 400s with a readable message otherwise.
 *
 * `epochFrom`/`epochTo` crop which epochs appear. They never re-baseline: the
 * surveyed baseline is always included and every movement figure stays
 * measured from it.
 *
 * The report is delivered into `mapId`'s layer tree: a placeholder layer appears
 * immediately and is replaced by the finished PDF, the same way the annotation
 * report works. There is nothing to poll here.
 */
export const generatePrismReport = (
  st: SpanTag,
  siteId: string,
  setId: string,
  mapId: string,
  name?: string,
  /** S3 key of a map image captured client-side, for the report's site photo. */
  mapImageS3Key?: string,
  /** Inclusive ISO bounds from `getPrismEpochs`. */
  range?: { from?: string; to?: string },
): Promise<
  APIResponse<{
    status: 'generating';
    loadingLayerId: string;
    epochs: number;
    prisms: number;
  }>
> => {
  return fetchAPI(
    stleaf(st, 'msprr'),
    `${base()}/${siteId}/reference-sets/${setId}/report`,
    {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify({
        mapId,
        ...(name ? { name } : {}),
        ...(mapImageS3Key ? { mapImageS3Key } : {}),
        ...(range?.from ? { epochFrom: range.from } : {}),
        ...(range?.to ? { epochTo: range.to } : {}),
      }),
    },
  );
};

export const referenceSetTemplateUrl = (
  kind: MapSiteReferenceSetKind,
): string => {
  return `${base()}/reference-set-template?kind=${kind}`;
};

export const mapSitesAPI = {
  createSite,
  listSites,
  getSite,
  updateSite,
  archiveSite,
  listSiteMaps,
  listAssignableMaps,
  addMapToSite,
  listSiteDemLayers,
  removeMapFromSite,
  matchSitesForMap,
  getSiteForMap,
  getReferenceSets,
  getReferenceSetsForMap,
  getPrismEpochs,
  uploadReferenceSetVersion,
  addReferenceSetsToMap,
  listReferencePoints,
  updateReferencePointStatus,
  generatePrismReport,
  updateReferencePointEmbankment,
  getTolerances,
  getToleranceHistory,
  saveTolerances,
  referenceSetTemplateUrl,
};
